import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Row } from '@app/ui';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ErrorBoundary from '@app/ErrorBoundary';
import { ClientHeader } from '@app/components/common/ClientHeader';
import { HeaderTabs } from '@app/components/common/HeaderTabs';

import { User } from '@app/types/api';
import connect from '@app/utils/connect';
import { SETTINGS_TABS } from './constants/tabs';

import Password from './views/Password/Password';
import { DefaultFilters } from './views/DefaultFilters/DefaultFilters';
import Region from './views/Region/Region';

import CrashedPage from '@app/components/errors/CrashedPage';
import './styles/Settings.css';

type SettingsProps = {
    user: User;
};

const Settings = ({ user }: SettingsProps) => {
    const { t } = useTranslation('settings');
    const [selectedTab, setSelectedTab] = useState(SETTINGS_TABS.PASSWORD.name);

    const client = useMemo(
        () => user?.clientsById[user?.selectedClientId] || null,
        [user],
    );

    const navigate = useNavigate();

    const path = location.pathname.split('/').pop();

    const translatedTabs = useMemo(() => {
        return Object.keys(SETTINGS_TABS).reduce(
            (acc, key) => {
                acc[key] = {
                    ...SETTINGS_TABS[key],
                    title: t(SETTINGS_TABS[key].title),
                };
                return acc;
            },
            {} as typeof SETTINGS_TABS,
        );
    }, [t]);

    const changeTab = (tab: string) => {
        navigate(`/c/${client.id}/account-settings/${tab}`);
    };

    useEffect(() => {
        if (client) {
            const possiblePaths = Object.values(SETTINGS_TABS).map(
                tab => tab.name,
            );

            if (possiblePaths.includes(path)) {
                setSelectedTab(path);
            } else {
                navigate(
                    `/c/${client.id}/account-settings/${SETTINGS_TABS.PASSWORD.name}`,
                );
            }
        }
    }, [path, client]);

    if (!user || !client) {
        return <>{t('userSettings.loading')}</>;
    }

    return (
        <ErrorBoundary FallbackComponent={CrashedPage}>
            <Flex className="settings-page" vertical style={{ padding: 24 }}>
                <Row>
                    <ClientHeader
                        client={client}
                        title={t('userSettings.pageTitle')}
                    />
                </Row>

                <Row>
                    <HeaderTabs
                        tabs={translatedTabs}
                        activeTab={selectedTab}
                        onChangeTab={changeTab}
                        client={client}
                    />
                </Row>

                {selectedTab === SETTINGS_TABS.PASSWORD.name ? (
                    <Password />
                ) : null}
                {selectedTab === SETTINGS_TABS.DEFAULT_FILTERS.name ? (
                    <DefaultFilters />
                ) : null}
                {selectedTab === SETTINGS_TABS.REGION.name ? <Region /> : null}
            </Flex>
        </ErrorBoundary>
    );
};

export default connect(['clients', 'user'])(Settings);
